import React, {Component} from 'react';
import {
  bool,
  func,
  instanceOf,
  shape,
  string,
} from 'prop-types';
import CalendarPopout from '../CalendarPopout';
import FilterPanel, {optionShape, applyFilterSelection} from './FilterPanel';
import classNames from 'classnames';
import styles from './SearchBar.css';
import Icon from '../../components/Icon';
import Select from '../../components/forms/Select';
import Option from '../../components/forms/Option';
import {calendar} from 'react-icons-kit/fa/calendar';
import {times} from 'react-icons-kit/fa/times';
import {search} from 'react-icons-kit/fa/search';
import {filter} from 'react-icons-kit/fa/filter';

export class SearchBar extends Component {
  static propTypes = {
    className: string,
    showAdvancedFilters: bool,
    showCalenderFilter: bool,
    onSearch: func.isRequired,
    onChangeQuery: func.isRequired,
    query: shape({
      searchText: shape({
        ...optionShape,
        text: string.isRequired,
        placeholderText: string,
      }).isRequired,
      fromDate: instanceOf(Date),
      thruDate: instanceOf(Date),
      scope: shape(optionShape),
      packageType: shape(optionShape),
      field: shape(optionShape),
      status: shape(optionShape),
    }).isRequired,
  };

  static defaultProps = {
    className: null,
    showAdvancedFilters: false,
    showCalenderFilter: true,
  }

  constructor(props) {
    super(props);
    this.onSearchInputKeyPress = this.onSearchInputKeyPress.bind(this);
    this.toggleCalendarPopout = this.toggleCalendarPopout.bind(this);
    this.toggleFilterPanelPopout = this.toggleFilterPanelPopout.bind(this);
    this.closeAllPopouts = this.closeAllPopouts.bind(this);
    this.onDateChange = this.onDateChange.bind(this);
    this.clearSearchText = this.clearSearchText.bind(this);
    this.onFilterChange = this.onFilterChange.bind(this);

    this.state = {
      isCalendarPopoutOpen: false,
      isFilterPanelOpen: false,
    };
  }

  renderSearchTextFilter = () => {
    const {filters} = this.props.query.searchText;

    if (filters.length) {
      return <Select selectedOptionIcon="checkmark" defaultValue="all" className={styles.searchTextSelect} onChange={this.onChangeSearchFilter}>
        {filters.map(({name, text}) => <Option key={name} label={text} value={name} />)}
      </Select>;
    }

    return null;
  }
  render() {
    const {isFilterPanelOpen, isCalendarPopoutOpen} = this.state;
    const {showAdvancedFilters, showCalenderFilter, className} = this.props;
    const {fromDate, thruDate, packageType, searchText} = this.props.query;
    const showClearBtn = !!searchText.text;
    const broadcastFilter = packageType && packageType.filters.find(f => f.name === 'broadcast');
    const isBroadcastModeEnabled = broadcastFilter && broadcastFilter.selected;
    const placeholderText = isBroadcastModeEnabled ? 'Unable to search broadcasts' : searchText.placeholderText;
    const searchInputStyles = classNames({
      [styles.baseSearchBar]: showAdvancedFilters,
      [styles.slimSearchBar]: !showAdvancedFilters || !showCalenderFilter,
      [styles.noFiltersSearchBar]: !showAdvancedFilters && !showCalenderFilter
    });
    const calendarButtonStyles = classNames({
      'js-calendarBtnIgnoreOutsideClick': true,
      [styles.activeCalendarButton]: fromDate || thruDate,
      [styles.calendarButton]: !(fromDate || thruDate),
      [styles.disabledCalendarButton]: isBroadcastModeEnabled,
    });
    const filterButtonStyles = classNames({
      'js-filterBtnIgnoreOutsideClick': true,
      [styles.filterButton]: true,
    });

    return (
      <div className={classNames(styles.container, className)}>
        <input
          type="text"
          className={searchInputStyles}
          value={searchText.text}
          disabled={isBroadcastModeEnabled}
          onChange={({target}) => this.onChangeSearchText(target.value)}
          onKeyPress={this.onSearchInputKeyPress}
          placeholder={placeholderText}
        />
        {showAdvancedFilters &&
          <a className={filterButtonStyles} onClick={this.toggleFilterPanelPopout}>
            <Icon icon={filter}/>
          </a>}
        {showCalenderFilter &&
          <a className={calendarButtonStyles}
            {...(isBroadcastModeEnabled ? {} : {onClick: this.toggleCalendarPopout})}>
            <Icon icon={calendar}/>
          </a>}
        {showClearBtn
          ? <a className={styles.clearButton} onClick={this.clearSearchText}>
            <Icon icon={times}/>
          </a>
          : <a className={styles.searchButton}>
            <Icon icon={search}/>
          </a>}
        {isFilterPanelOpen &&
          <div className={styles.popout}>
            <FilterPanel
              {...this.props.query}
              onOutsideClick={this.closeAllPopouts}
              outsideClickIgnoreClass="js-filterBtnIgnoreOutsideClick"
              onFilterChange={this.onFilterChange}
            />
          </div>}
        {isCalendarPopoutOpen &&
          <div className={styles.popout}>
            <CalendarPopout
              fromDate={fromDate ?? null}
              thruDate={thruDate ?? null}
              onDateChange={this.onDateChange}
              outsideClickIgnoreClass="js-calendarBtnIgnoreOutsideClick"
              onOutsideClick={this.closeAllPopouts}
            />
          </div>}
        {this.renderSearchTextFilter()}
      </div>
    );
  }
  onChangeQuery = (query) => {
    return this.props.onChangeQuery({...this.props.query, ...query});
  }
  onChangeSearchText = (text) => {
    return this.onChangeQuery({
      searchText: {
        ...this.props.query.searchText,
        text,
      },
    });
  }
  onChangeSearchFilter = (e) => {
    this.onChangeQuery({
      searchText: {
        ...this.props.query.searchText,
        filters: applyFilterSelection({
          name: e.target.value,
          isSelected: true,
          filters: this.props.query.searchText.filters,
        }),
      },
    });
  }
  onSearchInputKeyPress(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.props.onSearch(this.props.query);
    }
  }
  onDateChange({fromDate, thruDate}) {
    this.props.onSearch({
      ...this.props.query,
      fromDate,
      thruDate,
    });
  }
  onFilterChange(filterOptions) {
    this.props.onSearch({
      ...this.props.query,
      ...filterOptions,
    });
  }
  toggleCalendarPopout() {
    this.setState({
      isCalendarPopoutOpen: !this.state.isCalendarPopoutOpen,
    });
  }
  toggleFilterPanelPopout() {
    this.setState({
      isFilterPanelOpen: !this.state.isFilterPanelOpen,
    });
  }
  closeAllPopouts() {
    this.setState({
      isCalendarPopoutOpen: false,
      isFilterPanelOpen: false,
    });
  }
  clearSearchText() {
    const query = {
      ...this.props.query,
      searchText: {
        ...this.props.query.searchText,
        text: '',
      },
    };
    this.props.onSearch(query);
  }
}

export default SearchBar;
